import React from 'react';
import { useLocation } from 'react-router-dom';
import { ChatGlocke } from '../admin/topbar/ChatGlocke';
import { objektOrtAusPfad } from '../../lib/immobilien/sidebarNav';
import { MobileKopf } from './sidebar/MobileLeiste';

/**
 * Inhaltsbereich des Immobilienbereichs (Prototyp: main padding 30/32/64, max. 1240 px).
 *
 * Oben rechts stehen Timer und Chat-Glocke aus der klassischen Kopfleiste, damit
 * im neuen Design keine Funktion fehlt (Profil/Abmelden: Nutzerkarte in der Sidebar).
 *
 * Seiten, die noch nicht neu gestaltet sind, laufen in einer hellen Fläche wie bisher –
 * sie kennen keine Design-Tokens und wären auf dunklem Grund nicht lesbar.
 */

/** Pfade, deren Seite schon im neuen Design gebaut ist (wächst mit E2/E3 …). */
function istNeuGestaltet(pfad: string): boolean {
  if (pfad === '/admin/objekte' || pfad === '/admin/objekte/' || pfad.startsWith('/admin/kalender')) return true;
  if (pfad === '/admin/mieter' || pfad.startsWith('/admin/mieter/')) return true;
  if (pfad === '/admin/eigentuemer' || pfad.startsWith('/admin/eigentuemer/')) return true;
  if (pfad === '/admin/dienstleister' || pfad.startsWith('/admin/dienstleister/')) return true;
  if (pfad === '/admin/dokumente' || pfad.startsWith('/admin/dokumente/')) return true;
  if (pfad === '/admin/schreiben' || pfad.startsWith('/admin/schreiben/')) return true; // #586 Übersicht und Assistent
  if (pfad === '/admin/emails' || pfad.startsWith('/admin/emails/')) return true;
  if (pfad === '/admin/zaehler' || pfad.startsWith('/admin/zaehler/')) return true;
  if (pfad === '/admin/tickets' || pfad.startsWith('/admin/tickets/')) return true;
  if (pfad === '/admin/mieterselbstauskunft') return true;
  if (pfad === '/admin/vertraege' || pfad.startsWith('/admin/vertraege/')) return true; // #595 Verträge-Übersicht
  if (pfad === '/admin/uebergabeprotokolle' || pfad.startsWith('/admin/uebergabeprotokolle/')) return true;
  if (pfad === '/admin/rechnungen' || pfad.startsWith('/admin/rechnungen/')) return true;
  if (pfad === '/admin/zeiterfassung' || pfad === '/admin/abwesenheiten' || pfad === '/admin/personalakte' || pfad === '/admin/monatsabschluss') return true;
  if (pfad === '/admin/team' || pfad.startsWith('/admin/team/')) return true;
  if (pfad === '/admin/super-admin' || pfad === '/admin/profile' || pfad === '/admin/dashboard') return true;
  // Sonstige Kontakte: nur die Liste; /admin/kontakte/mieter/… bleibt im alten Layout
  if (pfad === '/admin/kontakte' || pfad === '/admin/kontakte/' || pfad.startsWith('/admin/kontakte/kontakt/')) return true;
  const ort = objektOrtAusPfad(pfad);
  return ort?.bereich === 'uebersicht' || ort?.bereich === 'einheiten';
}

export function ImmobilienInhalt({ children, mobil = null }: { children: React.ReactNode; mobil?: { quer: boolean } | null }) {
  const { pathname } = useLocation();
  const neu = istNeuGestaltet(pathname);
  // Mobile Ansicht mit unterer Leiste (Handoff Dokumente §7): Kopfleiste statt Menü-Knopf, Platz unten für die Leiste
  if (mobil && neu) {
    return (
      <main className="relative flex-1 min-w-0" style={{ zIndex: 1, padding: mobil.quer ? '12px 16px 88px' : '10px 14px 104px' }}>
        <MobileKopf extra={<ChatGlocke />} />
        <div className="mx-auto flex flex-col" style={{ maxWidth: 1240, gap: 18 }}>{children}</div>
      </main>
    );
  }
  return (
    <main className="relative flex-1 min-w-0 px-4 pt-16 pb-16 lg:px-8 lg:pt-[30px]" style={{ zIndex: 1 }}>
      <div
        className={neu ? 'absolute right-4 top-3 lg:right-8 lg:top-[30px] z-10 flex items-center gap-2' : 'flex items-center justify-end gap-2'}
        style={neu ? undefined : { minHeight: 32, marginBottom: 8 }}
      >
        <ChatGlocke />
      </div>
      {neu ? (
        <div className="mx-auto flex flex-col" style={{ maxWidth: 1240, gap: 22 }}>{children}</div>
      ) : (
        // Bewusst KEIN backdrop-filter/transform: beides macht die Fläche zum Bezugsrahmen
        // für position:fixed – die rechten Seitenleisten der alten Seiten hingen sonst hier fest.
        // overflow-x: clip schneidet Überstand ab wie das alte <main>, ohne sticky zu brechen.
        <div
          className="mx-auto text-[#0f172a]"
          style={{
            maxWidth: 1600, borderRadius: 26, padding: 16, background: 'rgba(255,255,255,0.86)', boxShadow: 'var(--glass-sh)',
            fontFamily: "'Montserrat', sans-serif", overflowX: 'clip',
          }}
        >
          {children}
        </div>
      )}
    </main>
  );
}
